// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Mail-delivery status, shared by every surface that reports whether something actually arrived.
 *
 * Extracted from SupportTeamMembersPage (2026-08-22) when Notification delivery needed the same
 * control. L-067: when a control exists on two surfaces, share the mounted unit.
 *
 * The rule this component exists to enforce: "we created a row" and "the mail left the building"
 * are DIFFERENT CLAIMS and must never render the same. Collapsing them is what let four failed
 * notifications on case #1015 sit invisible, and what let invitations look sent when no email had
 * ever been queued.
 *
 * `status === null` therefore means something specific and worse than 'queued': no mail row exists
 * at all. It renders as "never queued", not as pending.
 */
export default function DeliveryPill({ status, sentAt, error, attempts }: {
  /** Mail status, or null when NO mail row exists for the thing being reported on. */
  status: string | null
  sentAt?: string | null
  error?: string | null
  attempts?: number
}) {
  const pill = (bg: string, fg: string, text: string, note?: string) => (
    <>
      <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: bg, color: fg }}>{text}</span>
      {note && <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 3, wordBreak: 'break-word' }}>{note}</div>}
    </>
  )

  if (status == null) return pill('#fee2e2', '#991b1b', 'never queued', 'no email was created for this')
  if (status === 'sent') return pill('#dcfce7', '#15803d', 'sent', sentAt ? new Date(sentAt).toLocaleString('en-US') : undefined)

  // The REASON is the payload. A failure with no reason is the dead end this whole component
  // exists to remove, so fall back to a plain statement rather than rendering an empty note.
  const tries = attempts && attempts > 1 ? ` · ${attempts} attempts` : ''
  if (status === 'failed') return pill('#fee2e2', '#991b1b', 'failed', (error ?? 'no reason recorded') + tries)
  if (status === 'suppressed') return pill('#f5f5f4', '#57534e', 'suppressed', error ?? 'blocked before sending')
  return pill('#fef3c7', '#b45309', status, (error ?? 'queued, not yet sent') + tries)
}
